/**
 * Browser Geolocation API + free client-side reverse geocoding, used by ProfilePage's "use my
 * current location" button. Only ever produces a human-readable "City, Region" label — raw
 * coordinates are read locally and never sent anywhere except to the reverse-geocode call
 * itself, and are never persisted (see packages/database's `locationLabel` docblock for why).
 *
 * Reverse geocoding uses BigDataCloud's free, unauthenticated, CORS-enabled client-side
 * endpoint (https://www.bigdatacloud.com/docs/api/free-reverse-geocode-to-city-api) — no API
 * key or backend involvement needed, which keeps this a pure frontend feature.
 */

export class GeolocationError extends Error {}

function getCurrentPosition(): Promise<GeolocationPosition> {
  if (!("geolocation" in navigator)) {
    throw new GeolocationError("This browser doesn't support location detection.");
  }
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      resolve,
      (err) => {
        const message =
          err.code === err.PERMISSION_DENIED
            ? "Location access was denied. You can allow it in your browser's site settings, or just type your location in manually."
            : "Couldn't determine your location. Try again, or type it in manually.";
        reject(new GeolocationError(message));
      },
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 },
    );
  });
}

interface ReverseGeocodeResult {
  city?: string;
  locality?: string;
  principalSubdivision?: string;
  countryName?: string;
}

/** Returns a "City, Region" (or best-effort fallback) label, or throws `GeolocationError`. */
export async function detectLocationLabel(signal?: AbortSignal): Promise<string> {
  const position = await getCurrentPosition();
  const { latitude, longitude } = position.coords;

  const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`;
  let response: Response;
  try {
    response = await fetch(url, { signal });
  } catch {
    throw new GeolocationError("Found your location, but couldn't look up its name.");
  }
  if (!response.ok) {
    throw new GeolocationError("Found your location, but couldn't look up its name.");
  }

  const data = (await response.json()) as ReverseGeocodeResult;
  const place = data.city || data.locality;
  const region = data.principalSubdivision;
  if (place && region) return `${place}, ${region}`;
  if (place) return place;
  if (data.countryName) return data.countryName;
  throw new GeolocationError("Found your location, but couldn't determine a readable name.");
}
